/**
 * The Schools table of Product Insights · LAE (Schools mode), and the School modal's outcomes table
 * (C12 passes `columnGroups`). One row per school × funnel; each stacked column draws one line per
 * assignment, then a bold subtotal line, so line i sits at the same height across the row.
 *
 * Ports LAE `features/productivity/SchoolsTable.jsx`, the band row, group separators and footer of
 * `DataTable.jsx`, and the header of `TableToolbar.jsx`. Columns come from `./lae-school-columns`
 * (P7); rows and sort from `./lae-school-rows` (P6).
 *
 * Deliberate differences from LAE (spec §5):
 * - Sort, column filters and hidden columns are remembered on the account (`useTableFilters` memory
 *   and `useColumnLayout`), not in the URL. The sort comes from the header popovers; while none is
 *   chosen, `defaultSort` applies.
 * - Number columns carry `kind: 'number'`, so their popovers offer conditions; all filtering is
 *   client-side over the whole response (every column is page-scoped, and the page IS the dataset).
 * - School and Assignment are code-pinned left (always shown, always frozen).
 * - No zebra tint, no rubric bands, no team default, no definition tooltips (§0.3, §0.4).
 * - The Total row is the last body row, not a sticky footer: `TableWrap` has no sticky footer.
 * - The Split by assignment switch is a `Checkbox` (no switch primitive), shown only when the
 *   response has `groups` and the caller passes `onSplitChange`.
 * - The label row sits under a fixed-height band row, so its sticky top is raised by the band
 *   height with an important utility: `FilterableTH` forwards no `style`, and its baked top-0
 *   would otherwise race the offset by stylesheet order.
 * - `FilterableTH` takes no `title`, so the header shows the short label and the popover, the
 *   filter sheet and the column manager carry the full one.
 * - Below `md` the table becomes a card list with the same visible columns (subtotal values), a
 *   filter sheet and one sort Select.
 */
import { useMemo, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import {
  Badge,
  Button,
  Checkbox,
  ColumnManager,
  CountBadge,
  EmptyState,
  FilterSheet,
  FilterableTH,
  Select,
  TBody,
  TD,
  TH,
  THead,
  TR,
  Table,
  TableWrap,
  type FilterableTHProps,
  type FrozenSide,
} from '@/components/ui';
import { TextLink } from '@/components/ui/TextLink';
import { cx } from '@/lib/cx';
import { useColumnLayout, useFrozenOffsets } from '@/lib/use-column-layout';
import { useTableFilters, type ColumnSpec } from '@/lib/use-table-filters';
import type { LaeProdMetrics } from '@/lib/types';
import { PiCardField, PiCardList, PiExpandButton } from '../pi-kit';
import {
  SCHOOL_GROUPS,
  createSchoolColumns,
  schoolFooterCells,
  type SchoolColumn,
  type SchoolTableRow,
} from './lae-school-columns';
import {
  schoolRowsOf,
  sortSchoolRows,
  type LaeSchoolGroup,
  type LaeSchoolListRow,
  type LaeSchoolRow,
  type LaeSort,
  type SortDir,
} from './lae-school-rows';
import { fmtDay, fmtNum, fmtPct, splitSchool } from './lae-format';
import { SpecialistsLine } from './StackedCell';

export interface SchoolsTableProps {
  /** Heading on the left of the toolbar. Default `Schools`. */
  title?: string;
  /** `groups[]` (with `breakdown=assignments`); absent on an older response. */
  groups?: readonly LaeSchoolGroup[] | null;
  /** `schools[]`, used only when `groups` is absent. */
  schools?: readonly LaeSchoolListRow[] | null;
  /** Unfiltered person-deduplicated totals for the Total row. */
  totals?: Partial<LaeProdMetrics> | null;
  /** The page funnel, keying fallback rows built from `schools[]`. */
  funnel?: string | null;
  /** Show every assignment line (true) or only the consolidated line. */
  split: boolean;
  /** Present = the Split by assignment checkbox is shown (when `groups` exists). */
  onSplitChange?: (split: boolean) => void;
  /** Keep `school` plus only these bands (the modal's outcomes table). */
  columnGroups?: readonly string[];
  /** `useColumnLayout` scope (hidden / frozen columns, stored on the account). */
  scope: string;
  /** `useTableFilters` memory id (filters and sort, stored on the account). */
  memoryId: string;
  /** Sort while none is chosen in a header. */
  defaultSort: LaeSort;
  /** Row click: opens the School modal for that school and funnel. */
  onOpen?: (schoolId: string, funnel: string) => void;
  /** Extra toolbar controls (Export), given the filtered, sorted rows. */
  actions?: (visibleRows: LaeSchoolRow[]) => ReactNode;
  /** `false` = no Total row. Default true. */
  footer?: boolean;
  /** Action under the "No schools in this period" empty state (the page's Clear filters). */
  emptyAction?: ReactNode;
}

/** Code-pinned left: always first, visible and frozen. */
const PINNED_LEFT = ['school', 'assignment'] as const;

/** The band row's fixed height, which the label row's sticky top is raised by. */
const BAND_ROW = 'h-8';
const LABEL_ROW_TOP = 'top-8!';

/** A 1px rule on a cell's left edge, drawn by a pseudo-element so no baked shadow is raced. */
const SEPARATOR =
  'before:pointer-events-none before:absolute before:inset-y-0 before:left-0 before:w-px before:bg-hairline';

const isDuration = (key: string) => key.endsWith('_seconds');

function frozenPropsFor(
  key: string,
  frozenSideOf: (key: string) => FrozenSide | undefined,
  offsets: Record<string, number>,
): { frozen?: FrozenSide; frozenOffset?: number } {
  const side = frozenSideOf(key);
  if (side === undefined) return {};
  return { frozen: side, frozenOffset: offsets[key] ?? 0 };
}

const groupLabel = (id: string | undefined): string =>
  id && id in SCHOOL_GROUPS ? SCHOOL_GROUPS[id as keyof typeof SCHOOL_GROUPS] : '';

/** The row with its lines folded away, so a column renders only its subtotal (mobile cards). */
function subtotalOnly(row: LaeSchoolRow): LaeSchoolRow {
  if (row.__allLines) return row;
  return { ...row, assignments: [], __allLines: row.assignments };
}

const asTableRow = (row: LaeSchoolRow): SchoolTableRow => row as unknown as SchoolTableRow;

export function SchoolsTable({
  title = 'Schools',
  groups,
  schools,
  totals,
  funnel,
  split,
  onSplitChange,
  columnGroups,
  scope,
  memoryId,
  defaultSort,
  onOpen,
  actions,
  footer = true,
  emptyAction,
}: SchoolsTableProps) {
  const allRows = useMemo(() => schoolRowsOf(groups, schools, funnel, split), [groups, schools, funnel, split]);

  const groupsKey = columnGroups ? columnGroups.join(',') : '';
  const columns = useMemo<SchoolColumn[]>(
    () => createSchoolColumns({ groups: groupsKey ? groupsKey.split(',') : undefined }),
    [groupsKey],
  );
  const columnByKey = useMemo(() => new Map(columns.map((c) => [c.key, c])), [columns]);
  const keys = useMemo(() => columns.map((c) => c.key), [columns]);
  const pinnedLeft = useMemo(() => PINNED_LEFT.filter((k) => keys.includes(k)), [keys]);

  const layout = useColumnLayout(scope, keys, pinnedLeft, [], { pinnedLeftKeys: pinnedLeft });
  const { visibleKeys, frozenSideOf } = layout;
  const labelRowRef = useRef<HTMLTableRowElement>(null);
  const offsets = useFrozenOffsets(labelRowRef, visibleKeys, frozenSideOf);

  /* Every column is page-scoped: the value lists are built from the whole response. */
  const specs = useMemo<ColumnSpec<LaeSchoolRow>[]>(
    () =>
      columns.map((c) => {
        const spec: ColumnSpec<LaeSchoolRow> = {
          key: c.key,
          label: c.label,
          get: (r) => c.get(asTableRow(r)),
          sortKey: c.key,
          scope: 'page',
          numeric: c.numeric,
        };
        if (c.kind === 'number') spec.kind = 'number';
        if (isDuration(c.key)) spec.format = (v) => fmtNum(Number(v));
        return spec;
      }),
    [columns],
  );
  const filters = useTableFilters(allRows, specs, { memoryId });

  const activeSort = useMemo((): LaeSort => {
    const chosen = filters.sort;
    if (chosen && columnByKey.has(chosen.key)) return { key: chosen.key, dir: chosen.direction };
    if (columnByKey.has(defaultSort.key)) return defaultSort;
    const first = columns.find((c) => c.key !== 'school');
    return { key: first?.key ?? 'school', dir: 'desc' };
  }, [filters.sort, columnByKey, defaultSort, columns]);

  const rows = useMemo(() => {
    const col = columnByKey.get(activeSort.key);
    if (!col) return filters.visibleRows;
    return sortSchoolRows(filters.visibleRows, (r) => col.sortValue(asTableRow(r)), activeSort.dir);
  }, [filters.visibleRows, columnByKey, activeSort]);

  /* The header shows the short label; the popover keeps the full one. */
  const headerOf = (key: string): FilterableTHProps => {
    const header = filters.headerFor(key);
    const col = columnByKey.get(key);
    return col?.short ? { ...header, label: col.short } : header;
  };
  const labelOf = (key: string) => columnByKey.get(key)?.label ?? key;

  /* ---- bands and separators, from the current visible order ---- */
  const groupStart = new Set<string>();
  visibleKeys.forEach((key, i) => {
    if (i === 0) return;
    const prev = visibleKeys[i - 1];
    if (prev === undefined) return;
    if ((columnByKey.get(key)?.group ?? null) !== (columnByKey.get(prev)?.group ?? null)) groupStart.add(key);
  });
  /** Separator on a non-frozen group start whose left neighbour is not a frozen edge. */
  const separated = (key: string): boolean => {
    if (!groupStart.has(key) || frozenSideOf(key) !== undefined) return false;
    const prev = visibleKeys[visibleKeys.indexOf(key) - 1];
    return prev === undefined || frozenSideOf(prev) !== 'left';
  };

  interface Band {
    group: string | undefined;
    keys: string[];
    frozen: boolean;
  }
  const bands: Band[] = [];
  for (const key of visibleKeys) {
    const group = columnByKey.get(key)?.group;
    const frozen = frozenSideOf(key) !== undefined;
    const last = bands[bands.length - 1];
    if (last && !frozen && !last.frozen && group && last.group === group) last.keys.push(key);
    else bands.push({ group, keys: [key], frozen });
  }
  const hasBands = columns.some((c) => c.group);

  const showSplit = Boolean(onSplitChange) && Array.isArray(groups);
  const showFooter = footer && Boolean(totals);
  const footerCells = useMemo(() => (showFooter ? schoolFooterCells(totals) : {}), [showFooter, totals]);

  const filtered = filters.activeCount > 0;
  const countText = rows.length !== allRows.length ? `${rows.length} of ${allRows.length}` : `${allRows.length}`;

  const clearFiltersButton = (
    <Button
      variant="ghost"
      size="sm"
      onClick={filters.clearAll}
      aria-label={`Clear filters (${filters.activeCount})`}
    >
      <span className="inline-flex items-center gap-1.5">
        Clear filters
        <CountBadge count={filters.activeCount} />
      </span>
    </Button>
  );

  const empty =
    rows.length === 0 ? (
      allRows.length === 0 ? (
        <EmptyState
          title="No schools in this period"
          description="Choose a longer period or another cycle or funnel."
          action={emptyAction}
        />
      ) : (
        <EmptyState title="No rows match these filters" action={filtered ? clearFiltersButton : undefined} />
      )
    ) : null;

  const open = onOpen ? (row: LaeSchoolRow) => onOpen(row.school_id, row.funnel) : undefined;
  const onRowKey = (row: LaeSchoolRow) => (e: KeyboardEvent<HTMLTableRowElement>) => {
    if (e.target !== e.currentTarget) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      open?.(row);
    }
  };

  /* ---- mobile sort: one Select, every column in both directions ---- */
  const sortValue = `${activeSort.key}:${activeSort.dir}`;
  const onMobileSort = (value: string) => {
    const at = value.lastIndexOf(':');
    const key = value.slice(0, at);
    const dir = value.slice(at + 1) as SortDir;
    filters.headerFor(key).filter?.sort?.onSort(dir);
  };

  return (
    <div className="min-w-0">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <div className="flex min-w-0 items-baseline gap-2">
          <h3 className="text-sm font-semibold text-fg">{title}</h3>
          <span className="text-sm text-fg-muted tabular-nums">{countText}</span>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {filtered ? clearFiltersButton : null}
          <ColumnManager layout={layout} labelOf={labelOf} lockedKeys={pinnedLeft} />
          {showSplit ? (
            <Checkbox
              label="Split by assignment"
              checked={split}
              onChange={(e) => onSplitChange?.(e.target.checked)}
            />
          ) : null}
          {actions?.(rows)}
        </div>
      </div>

      {/* Below md: filters, sort and a card list in place of the table. */}
      <div className="mb-2 flex flex-wrap items-center gap-2 md:hidden">
        <FilterSheet
          keys={visibleKeys}
          headerFor={filters.headerFor}
          activeCount={filters.activeCount}
          clearAll={filters.clearAll}
          className="md:hidden"
        />
        <div className="min-w-0 flex-1">
          <Select aria-label="Sort by" value={sortValue} onChange={(e) => onMobileSort(e.target.value)}>
            {visibleKeys.map((key) => (
              <optgroup key={key} label={labelOf(key)}>
                <option value={`${key}:desc`}>{`${labelOf(key)} ↓`}</option>
                <option value={`${key}:asc`}>{`${labelOf(key)} ↑`}</option>
              </optgroup>
            ))}
          </Select>
        </div>
      </div>
      <div className="md:hidden">
        {empty ?? (
          <PiCardList
            items={showFooter ? [...rows, null] : rows}
            getKey={(r) => (r ? r.key : '__total')}
            render={(r) =>
              r ? (
                <SchoolCard
                  row={r}
                  split={split}
                  keys={visibleKeys}
                  columnByKey={columnByKey}
                  onOpen={open}
                />
              ) : (
                <div className="grid gap-1">
                  <p className="font-medium text-fg">Total</p>
                  {visibleKeys.map((key) =>
                    key === 'school' || footerCells[key] === undefined ? null : (
                      <PiCardField key={key} label={labelOf(key)} value={footerCells[key]} />
                    ),
                  )}
                </div>
              )
            }
          />
        )}
      </div>

      <TableWrap frozenHeader className="rounded-lg border border-border max-md:hidden">
        <Table>
          <THead>
            {hasBands ? (
              <TR>
                {bands.map((band, i) => {
                  const firstKey = band.keys[0] ?? '';
                  const continued = i > 0 && band.group !== undefined && bands[i - 1]?.group === band.group;
                  const label = band.group && !continued ? groupLabel(band.group) : '';
                  return (
                    <TH
                      key={`band-${firstKey}`}
                      colSpan={band.keys.length}
                      scope={band.group ? 'colgroup' : undefined}
                      {...frozenPropsFor(firstKey, frozenSideOf, offsets)}
                      className={cx(BAND_ROW, 'text-center', separated(firstKey) && SEPARATOR)}
                    >
                      {label}
                    </TH>
                  );
                })}
              </TR>
            ) : null}
            <TR ref={labelRowRef}>
              {visibleKeys.map((key) => (
                <FilterableTH
                  key={key}
                  {...headerOf(key)}
                  {...frozenPropsFor(key, frozenSideOf, offsets)}
                  className={cx(hasBands && LABEL_ROW_TOP, separated(key) && SEPARATOR)}
                />
              ))}
            </TR>
          </THead>
          <TBody>
            {empty ? (
              <TR>
                <TD colSpan={Math.max(visibleKeys.length, 1)}>{empty}</TD>
              </TR>
            ) : (
              rows.map((row) => (
                <TR
                  key={row.key}
                  interactive={Boolean(open)}
                  className={open ? 'cursor-pointer' : undefined}
                  onClick={open ? () => open(row) : undefined}
                  onKeyDown={open ? onRowKey(row) : undefined}
                  tabIndex={open ? 0 : undefined}
                  aria-label={open ? `Open ${row.school_name || row.school_id}` : undefined}
                >
                  {visibleKeys.map((key) => {
                    const col = columnByKey.get(key);
                    const sep = separated(key);
                    return (
                      <TD
                        key={key}
                        verticalAlign="top"
                        numeric={col?.numeric}
                        {...frozenPropsFor(key, frozenSideOf, offsets)}
                        className={cx(col?.numeric && 'whitespace-nowrap tabular-nums', sep && cx('relative', SEPARATOR))}
                      >
                        {col ? col.render(asTableRow(row)) : null}
                      </TD>
                    );
                  })}
                </TR>
              ))
            )}
            {showFooter && !empty ? (
              <TR className="bg-surface-sunken font-medium">
                {visibleKeys.map((key) => {
                  const col = columnByKey.get(key);
                  const sep = separated(key);
                  return (
                    <TD
                      key={key}
                      numeric={col?.numeric}
                      {...frozenPropsFor(key, frozenSideOf, offsets)}
                      className={cx(col?.numeric && 'whitespace-nowrap tabular-nums', sep && cx('relative', SEPARATOR))}
                    >
                      {footerCells[key] ?? null}
                    </TD>
                  );
                })}
              </TR>
            ) : null}
          </TBody>
        </Table>
      </TableWrap>
    </div>
  );
}

/** One mobile card: the school, then every visible column's subtotal, then the lines on demand. */
function SchoolCard({
  row,
  split,
  keys,
  columnByKey,
  onOpen,
}: {
  row: LaeSchoolRow;
  split: boolean;
  keys: readonly string[];
  columnByKey: ReadonlyMap<string, SchoolColumn>;
  onOpen?: (row: LaeSchoolRow) => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const folded = asTableRow(subtotalOnly(row));
  const name = row.school_name ?? '';
  const { primary, secondary } = splitSchool(name);
  const funnel = row.funnel_label || row.funnel;
  const company = row.emerge_company;
  const lines = split ? row.assignments : [];

  const heading = (linked: boolean) => (
    <div className="min-w-0">
      <p className="truncate font-medium text-fg" title={name || undefined}>
        {linked && company ? (
          <TextLink to={`/companies/${company.id}`} onClick={(e) => e.stopPropagation()}>
            {primary || name || '—'}
          </TextLink>
        ) : (
          primary || name || '—'
        )}
      </p>
      {secondary ? <p className="truncate text-xs text-fg-muted">{secondary}</p> : null}
      {funnel ? (
        <div className="mt-1">
          <Badge tone="neutral">{funnel}</Badge>
        </div>
      ) : null}
    </div>
  );

  const fields = (
    <div className="mt-2 grid gap-1">
      {keys.map((key) => {
        if (key === 'school') return null;
        const col = columnByKey.get(key);
        return col ? <PiCardField key={key} label={col.label} value={col.render(folded)} /> : null;
      })}
    </div>
  );

  /* A linked name is its own link, so it sits outside the card's open button. */
  const body = onOpen ? (
    <>
      {company ? heading(true) : null}
      <button
        type="button"
        className="block w-full min-w-0 rounded-md text-left focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent-brand"
        onClick={() => onOpen(row)}
      >
        {company ? null : heading(false)}
        {fields}
      </button>
    </>
  ) : (
    <>
      {heading(true)}
      {fields}
    </>
  );

  return (
    <div className="min-w-0">
      {body}
      {lines.length >= 2 ? (
        <div className="mt-2 border-t border-hairline pt-2">
          <div className="flex items-center gap-2">
            <PiExpandButton expanded={expanded} onClick={() => setExpanded((v) => !v)} label="Assignments" />
            <span className="text-xs text-fg-muted">{`${lines.length} assignments`}</span>
          </div>
          {expanded ? (
            <ul className="mt-2 grid gap-2">
              {lines.map((line, i) => (
                <li key={`${line.assignment_id}-${i}`} className="grid gap-1 rounded-md bg-surface-sunken p-2">
                  <p className="truncate text-xs font-medium text-fg">{line.name || line.assignment_type || '—'}</p>
                  <p className="text-xs text-fg-muted tabular-nums">
                    {`${fmtDay(line.start_date)} – ${fmtDay(line.end_date)}`}
                  </p>
                  <div className="min-w-0 text-xs">
                    <SpecialistsLine line={line} />
                  </div>
                  <PiCardField label="Dialled" value={fmtNum(line.calls)} />
                  <PiCardField label="Answer rate" value={fmtPct(line.connect_rate)} />
                  <PiCardField label="Marked" value={fmtNum(line.counselling_completed)} />
                  <PiCardField label="Positive" value={fmtNum(line.advanced)} />
                  <PiCardField label="Negative" value={fmtNum(line.regressed)} />
                  <PiCardField label="Started" value={fmtNum(line.applications)} />
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
